import { useEffect, useState } from 'react';
import { CognitoAuth, CognitoAuthSession } from 'amazon-cognito-auth-js';
import { getStage, Stage } from 'src/components/common/util/StageHelpers';
import {
  getAuthConfigByStage,
  AuthConfig,
} from 'src/components/common/util/AuthConfigHelpers';
import {
  savedOriginalUserLocationKey,
  readFromLocal,
  writeToLocal,
  setJwtToken,
  removeJwtToken,
} from 'src/api/preferencesClient';
import { useNavigate } from 'react-router';

export enum AuthenticationState {
  LOGIN_REQUIRED,
  LOGGING_IN,
  LOGGED_IN,
}

function useFederatedAuth() {
  const [authState, setAuthState] = useState(AuthenticationState.LOGGING_IN);
  const [userId, setUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  /* Helper functions */
  const resetLoginState = (authState: AuthenticationState) => {
    removeJwtToken();
    setUserId(null);
    setAuthState(authState);
  };

  let timer: number;
  const clearAutologOutTimer = () => {
    if (timer) {
      window.clearTimeout(timer);
    }
  };
  const autoLogOutAfter = (millies: number) => {
    console.info(`Session will expire in ${Math.floor(millies / 1000)}s`);
    clearAutologOutTimer(); //Clears the old timer
    timer = window.setTimeout(() => {
      resetLoginState(AuthenticationState.LOGIN_REQUIRED);
    }, millies);
  };

  // This code removes the "?code=..." from the URL.
  // It is because the grant code is not reusable. Sometimes
  // the SDK will report weird messages because of using old grant code.
  const updateHistory = () => {
    if (window.history.length > 0) {
      const originalLocation = readFromLocal(savedOriginalUserLocationKey) || {
        pathname: '/',
      };
      navigate(originalLocation, { replace: true });
    }
  };

  /* Cognito Auth Client*/
  const stage: Stage = getStage();
  const authConfig: AuthConfig = getAuthConfigByStage(stage);
  const auth: CognitoAuth = new CognitoAuth(authConfig);

  auth.useCodeGrantFlow();
  auth.userhandler = {
    onFailure: (err: any) => {
      console.error(`Cognito ${stage} onFailure`, err);
      updateHistory();
      resetLoginState(AuthenticationState.LOGIN_REQUIRED);
    },
    onSuccess: (result: CognitoAuthSession) => {
      const token = result.getIdToken();
      // Cognito/JWT `getExpiration()` returns epoch SECONDS per RFC 7519 —
      // deliberately different from LangHub API timestamps, which are epoch
      // milliseconds. Do not remove the *1000.
      const millies = token.getExpiration() * 1000 - Date.now();

      const userId =
        (token.decodePayload() as any).identities?.[0]?.userId || '';

      // Log only non-sensitive markers — never the result/session payload,
      // which carries the ID/access/refresh tokens.
      console.info(`Cognito ${stage} onSuccess`, {
        userId,
        expiresInSeconds: Math.floor(millies / 1000),
      });

      updateHistory();
      setJwtToken(token.getJwtToken());
      setUserId(userId);
      setAuthState(AuthenticationState.LOGGED_IN);
      autoLogOutAfter(millies);
    },
  };

  const login = (auth: CognitoAuth) => {
    const { href } = window.location;
    const session = auth.getSignInUserSession();

    if (session.isValid()) {
      const payload = session.getIdToken().decodePayload() as any;
      const userId = payload.identities?.[0]?.userId || '';
      // Log only the resolved userId — never the session, which carries tokens.
      console.info(`Cognito ${stage} valid session exists`, userId);
      setUserId(userId);
      setAuthState(AuthenticationState.LOGGED_IN);
      if (!timer) {
        // Cognito/JWT `getExpiration()` is epoch SECONDS per RFC 7519 —
        // deliberately different from LangHub API timestamps (milliseconds).
        const millies =
          session.getIdToken().getExpiration() * 1000 - Date.now();
        autoLogOutAfter(millies);
      }
      return true;
    }
    // Check specifically for 'code' query param since a blanket check for
    // query params will catch our user filters as well
    else if (href.indexOf('?code') > 0) {
      auth.parseCognitoWebResponse(href);
      return false;
    } else {
      console.info('Invalid session, redirecting to Auth');
      setAuthState(AuthenticationState.LOGGING_IN);
      // So we know to redirect the user back to where they wanted to go.
      // Persist an explicit shape: JSON.stringify(window.location) serializes to
      // "{}" because Location's fields are non-enumerable prototype accessors.
      writeToLocal(savedOriginalUserLocationKey, {
        pathname: window.location.pathname,
        search: window.location.search,
      });
      auth.clearCachedTokensScopes();
      auth.getSession();
      return false;
    }
  };

  useEffect(() => {
    login(auth);
    return clearAutologOutTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { authState, userId };
}

export default useFederatedAuth;
